import type { FC, ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import type { ImageSource } from 'expo-image';
import FansLogo from 'assets/images/themenfans-logo.png';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import { PageHeaderVariant } from 'src/domain/models/PageHeaderVariant';

export type Props = {
	description?: string | undefined;
	image?: ImageSource | number | undefined;
	thermenFansLogo?: boolean;
	title?: string | undefined;
	variant?: PageHeaderVariant;
	children?: ReactNode;
};

export const COMPACT_PAGE_HEADER_HEIGHT = Dimens.size218;

const PageHeader: FC<Props> = ({
	description,
	image,
	thermenFansLogo,
	title,
	variant = PageHeaderVariant.Default,
	children,
}) => {
	const isCompact = variant === PageHeaderVariant.Compact;

	return (
		<View style={[styles.component, isCompact ? styles.componentCompact : null]}>
			{image ? <ThermenImage source={image} style={styles.image} /> : null}

			{isCompact ? (
				<LinearGradient
					colors={[Colors.transparent, Colors.black80, Colors.black80]}
					locations={[Dimens.ratio54, Dimens.ratio87, Dimens.ratio100]}
					style={styles.gradientOverlayCompact}
				/>
			) : (
				<LinearGradient colors={[Colors.transparent, Colors.black66]} style={styles.gradientOverlay} />
			)}

			<View style={[styles.contents, isCompact ? styles.contentsCompact : null]}>
				{thermenFansLogo ? <ThermenImage source={FansLogo} style={styles.thermenFansLogo} /> : null}

				{title ? (
					<View style={[styles.contentContainer, isCompact ? styles.contentContainerCompact : null]}>
						<Text style={[styles.title, isCompact ? styles.titleCompact : null]}>{title}</Text>
					</View>
				) : null}

				{description ? (
					<View style={[styles.contentContainer, isCompact ? styles.contentContainerCompact : null]}>
						<Text style={styles.description}>{description}</Text>
					</View>
				) : null}

				{children}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	component: {
		backgroundColor: Colors.gray400,
		height: Dimens.size380,
		overflow: 'hidden',
		position: 'relative',
	},
	componentCompact: {
		height: COMPACT_PAGE_HEADER_HEIGHT,
	},
	image: {
		height: '100%',
		left: Dimens.padding0,
		position: 'absolute',
		top: Dimens.padding0,
		width: '100%',
	},
	thermenFansLogo: {
		alignSelf: 'center',
		height: Dimens.size50,
		position: 'relative',
		width: Dimens.size214,
	},
	title: {
		color: Colors.white,
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize24,
		lineHeight: Dimens.lineHeight32,
		textAlign: 'center',
	},
	titleCompact: {
		fontSize: Dimens.fontSize32,
		lineHeight: Dimens.lineHeight40,
		textAlign: 'left',
	},
	description: {
		color: Colors.white,
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight24,
		textAlign: 'center',
	},
	gradientOverlay: {
		bottom: Dimens.padding0,
		height: '80%',
		position: 'absolute',
		width: '100%',
	},
	gradientOverlayCompact: {
		bottom: Dimens.padding0,
		height: '100%',
		position: 'absolute',
		width: '100%',
	},
	contents: {
		position: 'relative',
		paddingHorizontal: Dimens.padding18,
		paddingBottom: Dimens.padding48,
		gap: Dimens.gap18,
		zIndex: Dimens.zIndexSticky,
		flex: Dimens.flex1,
		justifyContent: 'flex-end',
	},
	contentsCompact: {
		paddingBottom: Dimens.padding26,
	},
	contentContainer: {
		alignSelf: 'center',
		maxWidth: Dimens.size450,
	},
	contentContainerCompact: {
		alignSelf: 'stretch',
		maxWidth: undefined,
	},
});

export default PageHeader;
